/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Card, Checkbox, Loading, TextInput, Tooltip } from '@sede-enterprise/shell-ds-react-framework'
import { Download, InfoCircle, Search } from '@sede-enterprise/shell-ds-react-framework/build/esm/components/Icon/components'
import React, { useContext, useEffect, useState } from 'react'
import useGetRemnantLifeAlertsData from './hooks/useGetRemnantLifeAlertsData';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import useDebounce from '../../util/useDebounce';
import downloadRemnantAlerts from './util/downloadRemnantAlerts';
import { getCheckboxHeaderState, handleAllRowsSelection, handleCheckboxSelection, isRowSelected } from './util/alertsUtil';
import { markSelectedAsRead } from './util/markAlertsAsRead';
import { DE_INSTALL_HELP, DE_INSTALLED_PROD_UNIT } from '../../util/helperTexts';
import { splitToArray , downloadAsExcel} from '../../util/helperFunctions';
import { AuthContext } from '../../AuthProvider';

import * as R from "rambda";

const pageSize = 10;
const INIT_PAGE = 1;
const INIT_ORDER = "startDate";
const INIT_ORDER_DIRECTION = 1
const sensor_filter = `sensor.excludeFromUi = 0`;

export const downloadAsXLSX = async (sensorData, columns) => {
    if (sensorData?.alertsList?.length && columns?.length > 0) {
        const downloadData = sensorData.alertsList.map((row) => {
            return columns.reduce((prev, curr) => {
                let value = '';
                if (typeof curr.accessorFn === 'function') {
                    value = curr.accessorFn(row);
                } else if (curr.id && row.hasOwnProperty(curr.id)) {
                    value = row[curr.id];
                }
                return {
                    ...prev,
                    [typeof curr.header === 'string' ? curr.header : curr.id]: value,
                };
            }, {});
        });

        await downloadAsExcel([{ sheetName: 'Remnant Thickness Alerts', data: downloadData }], 'RemnantThicknessAlerts.xlsx');
    }
};

const RemnantAlertsTable = ({ filter }) => {
    const authContext = useContext(AuthContext)
    const isTokenReady = authContext?.isTokenReady;

    const [currentPage, setCurrentPage] = useState(+sessionStorage.getItem("remnentPage") || INIT_PAGE);
    const [sensorOrder, setSensorOrder] = useState(sessionStorage.getItem("remanentAlertOrder") || INIT_ORDER);
    const [orderDirection, setOrderDirection] = useState(+sessionStorage.getItem("remnantOrderDirection") || INIT_ORDER_DIRECTION);
    const orderString = orderDirection === 1 ? `DESC` : `ASC`;

    const [prevFilter, setPrevFilter] = useState(filter);

    const [refreshToggle, setRefreshToggle] = useState(false);


    const [searchVal, setSearchVal] = useState("");
    const debouncedSearchVal = useDebounce(searchVal, 500);
    const [sensorListFilter, setSensorListFilter] = useState(sessionStorage.getItem("alertApplyfilter") || sensor_filter);

    const [selectedRows, setSelectedRows] = useState([]);

    useEffect(() => {
        if (prevFilter !== filter) {
            setCurrentPage(INIT_PAGE);
            sessionStorage.removeItem('remnentPage');
        }
        setPrevFilter(filter);
    }, [filter]);

    useEffect(() => {
        if (debouncedSearchVal) {
            const filterValue = `${filter} AND (UPPER(sensor.name) LIKE UPPER('%${debouncedSearchVal}%') OR UPPER(sensor.conditionMonitoringLocation) LIKE UPPER('%${debouncedSearchVal}%')) AND ${sensor_filter}`;
            setSensorListFilter(filterValue);
            setCurrentPage(INIT_PAGE); // Reset the current page
            sessionStorage.removeItem('alertPage'); // Remove the current page from local storage
        } else {
            setSensorListFilter(filter);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchVal, filter]);


    const handleOrderChange = (sortBy, direction) => {
        setSensorOrder(sortBy);
        sessionStorage.setItem('remanentAlertOrder', sortBy);

        setOrderDirection(+direction);
        sessionStorage.setItem('remnantOrderDirectio', +direction);
    }

    const handleCurrentPageChange = (current) => {
        sessionStorage.setItem('remnentPage', current)
        setCurrentPage(current);
    }

    const { data: remnantAlertsData, loading } = useGetRemnantLifeAlertsData(sensorListFilter, pageSize, currentPage, sensorOrder,orderString, refreshToggle, isTokenReady);
    const { data: remnantAlertsDownloadData, error: downloadError, loading: downloadLoading } = useGetRemnantLifeAlertsData(sensorListFilter, 10000, 1, sensorOrder, orderString, isTokenReady);
    
    console.log("Remnant Alerts Data:", remnantAlertsData);


    // Function to trigger data refresh
    const refreshData = () => {
        setRefreshToggle(prev => !prev); // Flip the value to trigger useEffect
    };


    /** @type import('@tanstack/react-table').ColumnDef<any> */
    const columns = [
        {
            id: 'rowSelection',
            header: () => {
                const { isChecked, isIndeterminate } = getCheckboxHeaderState(remnantAlertsData, selectedRows);
                return <Checkbox indeterminate={isIndeterminate} checked={isChecked} onChange={(e) => handleAllRowsSelection(e, selectedRows, setSelectedRows, remnantAlertsData)} />
            },
            cell: (props) => {
                const row = props.row.original;
                const isSelectedRow = isRowSelected(selectedRows, row);
                return <Checkbox checked={isSelectedRow} onChange={handleCheckboxSelection(row, selectedRows, setSelectedRows)}
                    disabled={R.path(["status"], row) === "READ"}
                />
            }
        },
        {
            header: 'Start Date',
            id: 'startDate',
            accessorFn: (row) => row.startDate
        },
        {
            header: 'End Date',
            id: 'endDate',
            accessorFn: (row) => row.endDate
        },
        {
            header: 'Sensor',
            accessorFn: row => row.isRealSensor && row.name,
            id: 'sensor_id',
            cell: (props) => {
                const row = props.row.original;
                return <Link
                    className={
                        row.uninstalled
                            ? "link redContent"
                            : "link blueContent"
                    }
                    to={`/home/${encodeURIComponent(row.sensor_id)}/details`}
                    title={
                        row.uninstalled
                            ? R.path(
                                row.conditionMonitoringLocation
                            ) === DE_INSTALLED_PROD_UNIT
                                ? DE_INSTALL_HELP.bukam
                                : DE_INSTALL_HELP.other
                            : ""
                    }
                >
                    {row.isRealSensor && row.sensor_id}
                    {!row.isRealSensor && <span title="This CML has no sensor installed" className="redContent">No Sensor</span>}

                </Link>
            }
        },
        {
            header: 'CML',
            id: 'sensor.cmLocation.shortName',
            accessorFn: row => row.conditionMonitoringLocationName,
            cell: (props) => {
                const row = props.row.original;
                return row.conditionMonitoringLocationName ? (
                    <Link
                        className="link"
                        to={`/cmls/${encodeURIComponent(row.conditionMonitoringLocation)}/details`}
                    >

                        {row.conditionMonitoringLocationName}
                    </Link>
                ) : null
            },
        },
        {
            header: 'Remnant Life (year)',
            id: 'advanceNotificationPeriod',
            accessorFn: (row) => `${row.advanceNotificationPeriod} ${row.advanceNotificationPeriod > 1 ? 'Years' : 'Year'} `
        },
        {
            header: "Status",
            id: 'status',
            accessorFn: (row) => row.status
        }

    ]



    return (
        <div className='remnant-alerts-section'>
            <div className='content-header'>

                <div className='header-title-help'>
                    <h2>Remnant Life Alerts</h2>
                    <Tooltip className='remanent-tooltip' placement='bottom'
                        overlay={<div className='tooltip-content'
                            id='remanent-id-100'> A Remnant Life alert is generated by considering the higher value between the “long term/short term CRavg, dividing the RCA by this rate, and projecting that the component will reach its retirement threshold within one year. Start / End dates represent the period during which a Remnant Life Alert is generated. </div>}
                        trigger={<Button size='large' variant='transparent' iconOnly icon={<InfoCircle />} onClick={() => { }} />} />
                </div>
                <div className='header-actions'>
                    <Button onClick={() => markSelectedAsRead(selectedRows, refreshData, setSelectedRows)}
                        disabled={selectedRows.length === 0}
                    >Mark As Read</Button>
                    <div className='search-wrapper'>
                        <TextInput
                            prefix={{ node: <Search className='search-icon' /> }}
                            placeholder='Search by Sensor or CML name'
                            onChange={(e) => {
                                setSearchVal(e.target.value);
                            }}
                            value={searchVal}
                        />
                    </div>
                    <Tooltip id='id-5' overlay={<span id='home-id-6'>Download</span>}
                        placement='bottom'
                        trigger={<Button data-testid="download" iconOnly icon={<Download />} onClick={() => downloadAsXLSX(remnantAlertsDownloadData, columns)} id='sensor-list-alertid-14' />} />
                </div>
            </div>
            <div className='content-body'>
                {remnantAlertsData?.alertsList?.length <= 0 && loading && <div className='loading-container' role='status'><Loading /></div>}
                {!loading && remnantAlertsData?.alertsList?.length === 0 && <Card className='empty-table-body' bodyPadding={false}>
                    <p className='data-value'>No Data under this Category</p>
                </Card>}
                {remnantAlertsData?.alertsList?.length > 0 &&
                    <DataTable data={remnantAlertsData.alertsList} columns={columns} count={remnantAlertsData.alertsCount?.TotalRows}
                        currentPage={+sessionStorage.getItem("remnentPage") || INIT_PAGE}
                        handleCurrentPageChange={handleCurrentPageChange}
                        handleOrderChange={handleOrderChange}
                        INIT_ORDER={sessionStorage.getItem("remanentAlertOrder") || INIT_ORDER}
                        INIT_ORDER_DIRECTION={+sessionStorage.getItem("remnantOrderDirection") || INIT_ORDER_DIRECTION}
                        loading={loading}
                    />}
            </div>
        </div>
    )
}

export default RemnantAlertsTable